import { useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Section } from "./AiHtml";

const pill = { High: "text-red-300 bg-red-500/10", Medium: "text-amber-300 bg-amber-500/10", Low: "text-emerald-300 bg-emerald-500/10" };
const Tag = ({ v }) => <span className={`px-2 py-0.5 rounded-full text-[11px] whitespace-nowrap ${pill[v] || "text-slate-300 bg-slate-700/40"}`}>{v}</span>;
const Icon = ({ s }) => (s === "pass" || s === "good" ? <CheckCircle2 size={15} className="text-emerald-400 shrink-0" /> : s === "warn" || s === "warning" ? <AlertTriangle size={15} className="text-amber-400 shrink-0" /> : <XCircle size={15} className="text-red-400 shrink-0" />);

export const PerformanceStrip = ({ p }) => !p ? null : (
  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="audit-performance">
    {[["Response", `${p.response_ms} ms`], ["HTML size", `${p.html_kb} KB`], ["Redirects", p.redirects], ["Words", p.word_count], ["Images", p.images], ["No alt", p.images_no_alt], ["Internal links", p.internal_links], ["External links", p.external_links]].map(([l, v]) => (
      <div key={l} className="rounded-lg bg-slate-900 border border-slate-800 px-3 py-2"><p className="text-[10px] font-mono uppercase text-slate-500">{l}</p><p className="font-display font-semibold">{v}</p></div>
    ))}
  </div>
);

export const PriorityActions = ({ items }) => !items?.length ? null : (
  <Section title="Priority actions" testId="audit-priority-actions">
    <div className="space-y-3">{items.map((a, i) => (
      <div key={i} className="flex gap-3 border-b border-slate-800 pb-3 last:border-0">
        <span className="w-6 h-6 shrink-0 rounded-full bg-orange-500/15 text-orange-400 grid place-items-center text-xs font-mono">{i + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2"><p className="text-sm font-medium text-slate-100">{a.title}</p><Tag v={a.impact} /><span className="text-[11px] text-slate-500">effort {a.effort}</span></div>
          <p className="text-sm text-slate-400 mt-1">{a.detail}</p>
        </div>
      </div>))}
    </div>
  </Section>
);

export const Checklist = ({ checks }) => {
  const cats = [...new Set((checks || []).map((c) => c.category))];
  const [cat, setCat] = useState("All");
  if (!checks?.length) return null;
  const list = checks.filter((c) => cat === "All" || c.category === cat);
  const n = (s) => checks.filter((c) => c.status === s).length;
  return (
    <Section title="Technical checklist" testId="audit-checklist" right={<span className="text-xs font-mono text-slate-400"><span className="text-emerald-400">{n("pass")} pass</span> · <span className="text-amber-400">{n("warn")} warn</span> · <span className="text-red-400">{n("fail")} fail</span></span>}>
      <div className="flex flex-wrap gap-2 mb-4">{["All", ...cats].map((c) => <button key={c} data-testid={`checklist-filter-${c.toLowerCase()}`} onClick={() => setCat(c)} className={`pill ${cat === c ? "pill-on" : "pill-off"}`}>{c}</button>)}</div>
      <div className="divide-y divide-slate-800">{list.map((c, i) => (
        <div key={i} className="py-2.5 flex gap-3 text-sm">
          <Icon s={c.status} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap justify-between gap-2"><p className="text-slate-100">{c.label} <span className="text-[11px] text-slate-500">· {c.category}</span></p><p className="font-mono text-xs text-slate-400 break-all">{c.value}</p></div>
            {c.fix && <p className="text-xs text-slate-400 mt-0.5">{c.fix}</p>}
          </div>
        </div>))}
      </div>
    </Section>
  );
};

export const Opportunities = ({ r }) => (
  <div className="grid lg:grid-cols-2 gap-6">
    {r.keyword_opportunities?.length > 0 && (
      <Section title="Keyword opportunities" testId="audit-keywords">
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-[11px] font-mono uppercase text-slate-500"><th className="py-1.5">Keyword</th><th>Intent</th><th>Difficulty</th></tr></thead>
          <tbody>{r.keyword_opportunities.map((k, i) => <tr key={i} className="border-t border-slate-800 align-top"><td className="py-2 pr-2"><p className="text-slate-100">{k.keyword}</p><p className="text-xs text-slate-500">{k.why}</p></td><td className="text-xs text-slate-400 pr-2">{k.intent}</td><td><Tag v={k.difficulty} /></td></tr>)}</tbody></table></div>
      </Section>
    )}
    <div className="space-y-6">
      {r.competitors?.length > 0 && (
        <Section title="Competitors found" testId="audit-competitors">
          <ul className="space-y-3 text-sm">{r.competitors.map((c, i) => <li key={i}><p className="text-slate-100 font-medium">{c.name} <span className="text-xs text-slate-500 font-normal break-all">{c.website}</span></p><p className="text-slate-400">{c.note}</p></li>)}</ul>
        </Section>
      )}
      {r.online_presence?.length > 0 && (
        <Section title="Online presence (live search)" testId="audit-online-presence">
          <ul className="space-y-2.5">{r.online_presence.map((x, i) => <li key={i} className="flex gap-2.5 text-sm"><Icon s={x.status} /><div><p className="text-slate-100">{x.item}</p><p className="text-slate-400">{x.detail}</p></div></li>)}</ul>
        </Section>
      )}
    </div>
  </div>
);

export const PagesCrawled = ({ pages }) => !pages?.length ? null : (
  <Section title={`Pages crawled (${pages.length})`} testId="audit-pages">
    <div className="overflow-x-auto"><table className="w-full text-xs min-w-[560px]"><thead><tr className="text-left font-mono uppercase text-slate-500"><th className="py-1.5">URL</th><th>Status</th><th>Title</th><th>Meta</th><th>H1</th><th>Words</th></tr></thead>
      <tbody>{pages.map((p, i) => <tr key={i} className="border-t border-slate-800"><td className="py-2 pr-2 text-slate-300 break-all max-w-[220px]">{p.url}</td><td>{p.status}</td><td className="pr-2 text-slate-400 max-w-[200px] truncate">{p.title || "—"}</td><td>{p.meta_description ? "✓" : "✗"}</td><td>{p.h1}</td><td>{p.words}</td></tr>)}</tbody></table></div>
  </Section>
);
